import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Alert } from '@mui/material';
import { Counsellor } from '../../types/counsellor';
import api from '../../utils/api';
import { formatDateTime } from '../../utils/dateUtils';

interface AdminNotesProps {
  counsellor: Counsellor;
  onUpdate: () => void;
}

const AdminNotes: React.FC<AdminNotesProps> = ({ counsellor, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(counsellor.adminNotes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await api.put(`/api/counsellors/${counsellor._id}/admin-notes`, {
        adminNotes: notes
      });

      setSuccess('Admin notes saved successfully');
      setIsEditing(false);
      onUpdate();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save admin notes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(counsellor.adminNotes || '');
    setIsEditing(false);
    setError('');
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700, 
            color: 'rgba(255, 255, 255, 0.95)',
            mb: 1
          }}
        >
          Admin Notes
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
        >
          Private notes about this counsellor. Only visible to administrators.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box className="glass-card-light rounded-lg p-6">
        {isEditing ? (
          <>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this counsellor..."
              sx={{
                mb: 2,
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  color: 'white',
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6366f1',
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#6366f1',
                  '&:hover': {
                    backgroundColor: '#5558e3',
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save Notes'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        ) : (
          <>
            {counsellor.adminNotes ? (
              <Box>
                <Box 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 2,
                    p: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: 1,
                    minHeight: '100px'
                  }}
                >
                  {counsellor.adminNotes}
                </Box>
                {counsellor.adminNotesUpdatedAt && (
                  <Typography 
                    variant="caption" 
                    sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 2 }}
                  >
                    Last updated: {formatDateTime(counsellor.adminNotesUpdatedAt)}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={() => setIsEditing(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#6366f1',
                    '&:hover': {
                      backgroundColor: '#5558e3',
                    },
                  }}
                >
                  Edit Notes
                </Button>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography 
                  variant="body1" 
                  sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 2 }}
                >
                  No admin notes yet
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setIsEditing(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#6366f1',
                    '&:hover': {
                      backgroundColor: '#5558e3',
                    },
                  }}
                >
                  Add Notes
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminNotes;
